import { IButtonStyles, IconButton, ITextFieldStyles, Link, TextField } from "@fluentui/react";
import React, { CSSProperties, useCallback } from "react";

// Enhanced action button interface with better typing
interface ActionButton {
  disabled?: boolean;
  iconName: string;
  onClick: () => void;
  style?: CSSProperties;
  styles?: Partial<IButtonStyles>;
  title: string;
}

// Reusable TextField with action buttons and help link component
interface TextFieldWithActionsProps {
  actionButtonClassName?: string;
  actionButtons?: ActionButton[];
  containerClassName?: string;
  disabled?: boolean;
  helpLinkText?: string;
  label: string;
  onChange: (value: string) => void;
  onHelpLinkClick?: () => void;
  placeholder?: string;
  rows?: number;
  showHelpLink?: boolean;
  textFieldStyles?: Partial<ITextFieldStyles>;
  value: string;
}

export const TextFieldWithActions: React.FC<TextFieldWithActionsProps> = React.memo(
  ({
    label,
    value,
    onChange,
    rows = 10,
    actionButtons = [],
    showHelpLink = false,
    helpLinkText = "",
    onHelpLinkClick,
    containerClassName,
    textFieldStyles,
    actionButtonClassName,
    placeholder,
    disabled = false
  }) => {
    // Memoized keyboard handler
    const handleKeyDown = useCallback(
      (e: React.KeyboardEvent) => {
        if ((e.key === "Enter" || e.key === " ") && onHelpLinkClick) {
          e.preventDefault();
          onHelpLinkClick();
        }
      },
      [onHelpLinkClick]
    );

    // Memoized change handler
    const handleChange = useCallback(
      (e: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string) => {
        onChange(newValue ?? "");
      },
      [onChange]
    );

    // Show help link only when there's content and help is enabled
    const shouldShowHelpLink = showHelpLink && value.trim() && helpLinkText && onHelpLinkClick;

    return (
      <div className={containerClassName}>
        <TextField
          label={label}
          style={{ padding: "21px" }}
          multiline
          resizable={false}
          value={value}
          onChange={handleChange}
          rows={rows}
          styles={textFieldStyles}
          placeholder={placeholder}
          disabled={disabled}
        />

        {actionButtons.length > 0 && (
          <div style={{ position: "absolute", top: 0, right: 10, display: "flex" }}>
            {actionButtons.map((button, index) => (
              <IconButton
                ariaLabel={button.title}
                className={actionButtonClassName}
                disabled={button.disabled || disabled}
                iconProps={{ iconName: button.iconName }}
                key={`${button.iconName}-${index}`}
                onClick={button.onClick}
                styles={button.styles}
                title={button.title}
                style={button.style}
              />
            ))}
          </div>
        )}

        {shouldShowHelpLink && (
          <div
            style={{
              alignItems: "center",
              display: "flex",
              gap: 8,
              marginTop: 8
            }}
          >
            <img
              alt="tip"
              src="data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='16' height='16'><text x='0' y='14' font-size='16'>💡</text></svg>"
            />
            <Link
              onClick={onHelpLinkClick}
              onKeyDown={handleKeyDown}
              styles={{ root: { color: "var(--colorBrandBackgroundHover)" } }}
              tabIndex={0}
            >
              {helpLinkText}
            </Link>
          </div>
        )}
      </div>
    );
  }
);

TextFieldWithActions.displayName = "TextFieldWithActions";
